import { useCallback, useState } from "react";
import JSZip from "jszip";
import { Klydo } from "../../../api/klydo";

// The filename is the last path segment of the loopUrl (its ".mp4" basename),
// with any query string stripped: ".../gifsV3/abc123.mp4?x=1" -> "abc123.mp4".
const loopFileName = (loopUrl: string): string => {
  const last = loopUrl.split("/").pop() ?? "";
  return last.split("?")[0];
};

// Fetch with a bounded concurrency so we don't fire hundreds of large-video
// requests at once. onDone ticks after each settle for progress reporting.
async function forEachLimited<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
  onDone: () => void,
): Promise<void> {
  let cursor = 0;
  const runNext = async (): Promise<void> => {
    const index = cursor;
    cursor += 1;
    if (index >= items.length) return;
    try {
      await worker(items[index]);
    } finally {
      onDone();
    }
    await runNext();
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => runNext()),
  );
}

const useDownloadFirmwareKlydosLoops = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  // { done, total } so the caller can show progress on a long download.
  const [progress, setProgress] = useState<{ done: number; total: number }>({
    done: 0,
    total: 0,
  });

  const downloadLoops = useCallback(
    async (klydos: Klydo[], firmwareName: string) => {
      const withLoop = klydos.filter((k) => !!k.loopUrl);
      if (!withLoop.length) return;

      setIsDownloading(true);
      setProgress({ done: 0, total: withLoop.length });
      try {
        const zip = new JSZip();
        const usedNames = new Set<string>();
        let added = 0;

        await forEachLimited(
          withLoop,
          6,
          async (klydo) => {
            const response = await fetch(klydo.loopUrl);
            if (!response.ok) {
              throw new Error(`Failed to fetch loop for ${klydo.id}`);
            }
            const blob = await response.blob();

            // Basenames are unique cloudinary ids, but guard collisions anyway
            // by falling back to the klydo id.
            let name = loopFileName(klydo.loopUrl) || `${klydo.id}.mp4`;
            if (usedNames.has(name)) name = `${klydo.id}.mp4`;
            usedNames.add(name);

            zip.file(name, blob);
            added += 1;
          },
          () => setProgress((p) => ({ ...p, done: p.done + 1 })),
        );

        if (added === 0) {
          throw new Error("No loops could be fetched for this firmware");
        }

        const safeName = (firmwareName || "firmware").replace(
          /[^a-zA-Z0-9_-]/g,
          "_",
        );
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const objectUrl = URL.createObjectURL(zipBlob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `${safeName}-klydos.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error("Failed to download firmware klydos loops:", error);
      } finally {
        setIsDownloading(false);
      }
    },
    [],
  );

  return { downloadLoops, isDownloading, progress };
};

export default useDownloadFirmwareKlydosLoops;
